import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, Globe, MapPin, Save, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function BedrijfsInfoPage() {
  const { api } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api('get', '/api/platform/bedrijfsinfo');
        setInfo(r.data);
      } catch { setInfo({}); }
      setLoading(false);
    })();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api('post', '/api/platform/bedrijfsinfo', info);
      toast.success('Bedrijfsgegevens opgeslagen');
    } catch {
      toast.error('Opslaan mislukt');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Laden...</div>;

  const Field = ({ label, field, placeholder, icon: Icon, type = 'text', span2 }) => (
    <div style={span2 ? { gridColumn: '1 / -1' } : {}}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        {Icon && <Icon size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />}
        <input
          type={type}
          value={info[field] || ''}
          onChange={e => setInfo({ ...info, [field]: e.target.value })}
          placeholder={placeholder}
          data-testid={`field-${field}`}
          style={{
            width: '100%', padding: `12px ${Icon ? '12px' : '12px'} 12px ${Icon ? '38px' : '12px'}`,
            borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '14px', color: '#0f172a',
            outline: 'none', transition: 'border 0.2s', background: 'white',
          }}
          onFocus={e => e.target.style.borderColor = '#D4A017'}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
      </div>
    </div>
  );

  return (
    <div data-testid="bedrijfsinfo-page" style={{ maxWidth: '700px' }}>
      <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
        Bedrijfsinformatie
      </h1>
      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
        Pas uw bedrijfsgegevens aan. Deze worden gebruikt voor uw analyse en facturen.
      </p>

      {/* Bedrijfsgegevens */}
      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Building2 size={16} style={{ color: '#D4A017' }} />
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Bedrijfsgegevens</h2>
        </div>
        <div className="form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <Field label="Bedrijfsnaam" field="company_name" placeholder="Uw bedrijfsnaam" icon={Building2} />
          <Field label="Website URL" field="website_url" placeholder="https://uwbedrijf.nl" icon={Globe} />
          <Field label="Branche" field="branche" placeholder="Uw branche" />
          <Field label="Vestigingsplaats" field="locatie" placeholder="Stad" icon={MapPin} />
          <Field label="Telefoonnummer" field="telefoon" placeholder="+31 6 12345678" type="tel" />
          <Field label="KvK-nummer" field="kvk_nummer" placeholder="12345678" />
        </div>
        <div style={{ marginBottom: '0' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Bedrijfsomschrijving</label>
          <textarea
            value={info.beschrijving || ''}
            onChange={e => setInfo({ ...info, beschrijving: e.target.value })}
            placeholder="Korte beschrijving van uw bedrijf en diensten..."
            rows={3}
            data-testid="field-beschrijving"
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #e2e8f0',
              fontSize: '14px', color: '#0f172a', outline: 'none', resize: 'vertical', background: 'white',
            }}
            onFocus={e => e.target.style.borderColor = '#D4A017'}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
      </div>

      {/* Factuurgegevens */}
      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <FileText size={16} style={{ color: '#D4A017' }} />
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Factuurgegevens</h2>
        </div>
        <div className="form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Field label="BTW-nummer" field="btw_nummer" placeholder="NL123456789B01" />
          <Field label="Factuuradres" field="factuur_adres" placeholder="Straatnaam 123" />
          <Field label="Postcode" field="factuur_postcode" placeholder="1234 AB" />
          <Field label="Stad" field="factuur_stad" placeholder="Amsterdam" icon={MapPin} />
          <Field label="Land" field="factuur_land" placeholder="Nederland" />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          data-testid="save-bedrijfsinfo"
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px',
            borderRadius: '10px', border: 'none', background: '#0f172a', color: 'white',
            fontSize: '14px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>
    </div>
  );
}
